import { useState, useEffect } from 'react';
import { FormField, FormLabel, FormSelect, FormInput } from '../ui/Form';
import { Search, X, Download, Trash2 } from 'lucide-react';
import Button from '../ui/Button';
import type { MessageScheduleFilters } from '../../types/message';
import type { Assistant } from '../../types/assistant';
import type { WhatsAppSession } from '../../types/api';
import type { MessageQueue } from '../../types/messageQueue';
import type { MessageTemplate } from '../../types/messageTemplate';

interface MessageFiltersProps {
  appliedFilters: MessageScheduleFilters;
  onSearch: (filters: MessageScheduleFilters) => void;
  onClear: () => void;
  assistants?: Assistant[];
  sessions?: WhatsAppSession[];
  queues?: MessageQueue[];
  templates?: MessageTemplate[];
  onExport?: () => void;
  onClearMessages?: () => void;
  totalMessages?: number;
  isExporting?: boolean;
}

function buildInitialState(applied: MessageScheduleFilters): Record<string, string> {
  return {
    message: applied.message || '',
    send: applied.send?.toString() || '',
    sendtoPhone: applied.sendtoPhone || '',
    sendToMail: applied.sendToMail || '',
    assistant_id: applied.assistant_id?.toString() || '',
    whatsapp_id: applied.whatsapp_id?.toString() || '',
    queue_id: applied.queue_id?.toString() || '',
    template_id: applied.template_id?.toString() || '',
    scheduled_at_start: applied.scheduled_at_start || '',
    scheduled_at_end: applied.scheduled_at_end || '',
  };
}

function MessageFilters({
  appliedFilters,
  onSearch,
  onClear,
  assistants,
  sessions,
  queues,
  templates,
  onExport,
  onClearMessages,
  totalMessages = 0,
  isExporting = false,
}: MessageFiltersProps) {
  const [values, setValues] = useState<Record<string, string>>(buildInitialState(appliedFilters));

  useEffect(() => {
    setValues(buildInitialState(appliedFilters));
  }, [appliedFilters]);

  const handleChange = (name: string, value: string) => {
    setValues(prev => ({ ...prev, [name]: value }));
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const filters: MessageScheduleFilters = {};
    if (values.message) filters.message = values.message;
    if (values.send) filters.send = values.send === 'true';
    if (values.sendtoPhone) filters.sendtoPhone = values.sendtoPhone;
    if (values.sendToMail) filters.sendToMail = values.sendToMail;
    if (values.assistant_id) filters.assistant_id = Number(values.assistant_id);
    if (values.whatsapp_id) filters.whatsapp_id = Number(values.whatsapp_id);
    if (values.queue_id) filters.queue_id = Number(values.queue_id);
    if (values.template_id) filters.template_id = Number(values.template_id);
    if (values.scheduled_at_start) filters.scheduled_at_start = values.scheduled_at_start;
    if (values.scheduled_at_end) filters.scheduled_at_end = values.scheduled_at_end;
    onSearch(filters);
  };

  const handleClear = () => {
    setValues(buildInitialState({}));
    onClear();
  };

  return (
    <form onSubmit={handleSearch} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <FormField>
          <FormLabel htmlFor="message">Mensagem</FormLabel>
          <FormInput
            id="message"
            name="message"
            value={values.message}
            onChange={e => handleChange('message', e.target.value)}
            placeholder="Pesquisar por mensagem..."
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="send">Status</FormLabel>
          <FormSelect
            id="send"
            name="send"
            value={values.send}
            onChange={e => handleChange('send', e.target.value)}
          >
            <option value="">Todos</option>
            <option value="true">Enviado</option>
            <option value="false">Pendente</option>
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="sendtoPhone">Telefone</FormLabel>
          <FormInput
            id="sendtoPhone"
            name="sendtoPhone"
            value={values.sendtoPhone}
            onChange={e => handleChange('sendtoPhone', e.target.value)}
            placeholder="Pesquisar por telefone..."
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="sendToMail">Email</FormLabel>
          <FormInput
            id="sendToMail"
            name="sendToMail"
            value={values.sendToMail}
            onChange={e => handleChange('sendToMail', e.target.value)}
            placeholder="Pesquisar por email..."
          />
        </FormField>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <FormField>
          <FormLabel htmlFor="assistant_id">Assistente</FormLabel>
          <FormSelect
            id="assistant_id"
            name="assistant_id"
            value={values.assistant_id}
            onChange={e => handleChange('assistant_id', e.target.value)}
          >
            <option value="">Todos os assistentes</option>
            {assistants?.map(assistant => (
              <option key={assistant.id} value={assistant.id}>
                {assistant.name}
              </option>
            ))}
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="whatsapp_id">Conexão WhatsApp</FormLabel>
          <FormSelect
            id="whatsapp_id"
            name="whatsapp_id"
            value={values.whatsapp_id}
            onChange={e => handleChange('whatsapp_id', e.target.value)}
          >
            <option value="">Todas as conexões</option>
            {sessions?.map(session => (
              <option key={session.id} value={session.id}>
                {session.session_name}
              </option>
            ))}
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="queue_id">Fila</FormLabel>
          <FormSelect
            id="queue_id"
            name="queue_id"
            value={values.queue_id}
            onChange={e => handleChange('queue_id', e.target.value)}
          >
            <option value="">Todas as filas</option>
            {queues?.map(queue => (
              <option key={queue.id} value={queue.id}>
                {queue.name}
              </option>
            ))}
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="template_id">Template</FormLabel>
          <FormSelect
            id="template_id"
            name="template_id"
            value={values.template_id}
            onChange={e => handleChange('template_id', e.target.value)}
          >
            <option value="">Todos os templates</option>
            {templates?.map(template => (
              <option key={template.id} value={template.id}>
                {template.name || template.category_description || `Template ${template.id}`}
              </option>
            ))}
          </FormSelect>
        </FormField>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <FormField>
          <FormLabel htmlFor="scheduled_at_start">Agendado de</FormLabel>
          <FormInput
            id="scheduled_at_start"
            name="scheduled_at_start"
            type="date"
            value={values.scheduled_at_start}
            onChange={e => handleChange('scheduled_at_start', e.target.value)}
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="scheduled_at_end">Agendado até</FormLabel>
          <FormInput
            id="scheduled_at_end"
            name="scheduled_at_end"
            type="date"
            value={values.scheduled_at_end}
            onChange={e => handleChange('scheduled_at_end', e.target.value)}
          />
        </FormField>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex space-x-3">
          {onExport && (
            <Button
              type="button"
              variant="success"
              onClick={onExport}
              disabled={totalMessages === 0 || isExporting}
              isLoading={isExporting}
            >
              <Download className="h-4 w-4 mr-2" />
              Exportar ({totalMessages})
            </Button>
          )}
          {onClearMessages && (
            <Button
              type="button"
              variant="danger"
              onClick={onClearMessages}
              disabled={totalMessages === 0}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Excluir ({totalMessages})
            </Button>
          )}
        </div>
        <div className="flex space-x-3">
          <Button type="button" variant="secondary" onClick={handleClear}>
            <X className="h-4 w-4 mr-2" />
            Limpar Filtros
          </Button>
          <Button type="submit">
            <Search className="h-4 w-4 mr-2" />
            Pesquisar
          </Button>
        </div>
      </div>
    </form>
  );
}

export default MessageFilters;
